import React, { useMemo } from "react";

/**
 * BirthTimePicker - explicit Hour + Minute + AM/PM to eliminate AM/PM confusion.
 * Stores value as 24-hour "HH:MM" string and emits onChange with that format.
 */
export default function BirthTimePicker({ value, onChange, testIdPrefix = "birth-time" }) {
    // Parse "HH:MM" into 12h parts
    const parts = useMemo(() => {
        const [hStr = "12", mStr = "00"] = (value || "12:00").split(":");
        const h24 = parseInt(hStr, 10);
        const m = parseInt(mStr, 10);
        let h12 = h24 % 12;
        if (h12 === 0) h12 = 12;
        const period = h24 < 12 ? "AM" : "PM";
        return { h12, m, period };
    }, [value]);

    const emit = (h12, m, period) => {
        let h24 = h12 % 12;
        if (period === "PM") h24 += 12;
        const hh = String(h24).padStart(2, "0");
        const mm = String(m).padStart(2, "0");
        onChange(`${hh}:${mm}`);
    };

    const display24 = (() => {
        let h = parts.h12 % 12;
        if (parts.period === "PM") h += 12;
        return `${String(h).padStart(2, "0")}:${String(parts.m).padStart(2, "0")}`;
    })();

    const cls = "px-3 py-3 rounded-xl bg-[#0A0A1A] border border-white/10 focus:border-gold outline-none text-center text-foreground font-sans";

    return (
        <div>
            <div className="grid grid-cols-3 gap-2">
                <select
                    value={parts.h12}
                    onChange={(e) => emit(parseInt(e.target.value, 10), parts.m, parts.period)}
                    className={cls}
                    data-testid={`${testIdPrefix}-hour`}
                    aria-label="Hour"
                >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                        <option key={h} value={h}>{h}</option>
                    ))}
                </select>
                <select
                    value={parts.m}
                    onChange={(e) => emit(parts.h12, parseInt(e.target.value, 10), parts.period)}
                    className={cls}
                    data-testid={`${testIdPrefix}-minute`}
                    aria-label="Minute"
                >
                    {Array.from({ length: 60 }, (_, i) => i).map((m) => (
                        <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                    ))}
                </select>
                <select
                    value={parts.period}
                    onChange={(e) => emit(parts.h12, parts.m, e.target.value)}
                    className={cls}
                    data-testid={`${testIdPrefix}-period`}
                    aria-label="AM or PM"
                >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                </select>
            </div>
            <p className="text-xs text-gold mt-2 label-tracking" data-testid={`${testIdPrefix}-display`}>
                ✦ {parts.h12}:{String(parts.m).padStart(2, "0")} {parts.period}
                <span className="text-muted ml-2">({display24} · 24h)</span>
            </p>
        </div>
    );
}
